import React from "react";
import { motion } from "framer-motion";
import {
  Target,
  Users,
  BookOpen,
  ExternalLink,
  ShieldCheck,
  Brain,
  CheckCircle2,
  Route,
  Clock,
  AlertTriangle,
  Award,
  ClipboardCheck,
  TrendingUp,
  Zap,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { PageLayout } from "../components/Layout";
import { SEO, PageSEO } from "../components/SEO";

const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0 },
};

export const AboutPage = () => {
const practiceAreas = [
  {
    icon: Target,
    title: "Attention and concentration",
    text: "Builds skills relevant to Group Bourdon and TEA-Occ style demands: focus, scanning accuracy, selective attention and consistency.",
  },
  {
    icon: Clock,
    title: "Vigilance and sustained focus",
    text: "Builds skills relevant to WAFV Vigilance: staying alert, maintaining accuracy and responding to infrequent stimuli over time.",
  },
  {
    icon: Brain,
    title: "Memory and reasoning",
    text: "Builds skills relevant to TRP1 and TRP2: learning information, recalling rules, processing instructions and applying logic under time pressure.",
  },
  {
    icon: Route,
    title: "Perception and visual processing",
    text: "Builds skills relevant to ATAVT: quickly identifying relevant visual information and making accurate decisions from limited exposure.",
  },
  {
    icon: Zap,
    title: "Reaction speed and coordination",
    text: "Builds skills relevant to WAFV, ATAVT and 2HAND: fast responses, controlled movements and accuracy under pressure.",
  },
{
  icon: Users,
  title: "MMI and behavioural readiness",
  text: (
    <>
      Builds understanding relevant to the{" "}
      <a
        href="https://www.traindriverfoundation.com/online-assessment"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#0B1D51] font-semibold hover:text-[#FF9F1C] underline underline-offset-2"
      >
        SJE
      </a>{" "}
      and{" "}
      <a
        href="https://www.traindriverfoundation.com/interview"
        target="_blank"
        rel="noopener noreferrer"
        className="text-[#0B1D51] font-semibold hover:text-[#FF9F1C] underline underline-offset-2"
      >
        MMI
      </a>{" "}
      by helping candidates recognise safe behaviours, structured
      judgement and role-appropriate responses.
    </>
  ),
},
];

  const principles = [
    "Rail-focused preparation built specifically for aspiring train drivers.",
    "Interactive practice across the core skill areas linked to psychometric assessment.",
    "Repeated exercises designed to improve familiarity, accuracy and confidence.",
    "Clear feedback so candidates know where to focus their preparation.",
  ];

  return (
    <PageLayout>
      <SEO {...PageSEO.about} />

      <main className="min-h-screen bg-[#F8FAFC] text-slate-800">
        {/* Hero */}
        <section className="relative overflow-hidden bg-[#0B1D51] text-white">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#FF9F1C] blur-3xl" />
            <div className="absolute bottom-0 -left-24 h-72 w-72 rounded-full bg-[#2CB67D] blur-3xl" />
          </div>

          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              transition={{ duration: 0.45 }}
              className="max-w-4xl"
            >
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-medium text-white/90 ring-1 ring-white/20 mb-6">
                <ShieldCheck className="h-4 w-4 text-[#FF9F1C]" />
                Built for aspiring UK train driver applicants
              </div>

              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                Master the skills before the assessment tests them
              </h1>

              <p className="text-lg sm:text-xl text-white/82 leading-relaxed max-w-3xl">
                Train Driver Psychometrics is a rail-focused practice platform
                designed to help aspiring train drivers improve the key cognitive,
                attention and reaction skills assessed during the recruitment
                process.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a
                  href="/register"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#FF9F1C] px-6 py-3 font-semibold text-[#0B1D51] hover:bg-[#ffb347] transition-colors"
                >
                  Start practising now
                  <CheckCircle2 className="h-5 w-5" />
                </a>

                <a
                  href="https://www.traindriverfoundation.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-white/10 px-6 py-3 font-semibold text-white ring-1 ring-white/20 hover:bg-white/15 transition-colors"
                >
                  Visit Train Driver Foundation
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Intro */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="grid lg:grid-cols-3 gap-6">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              transition={{ delay: 0.1 }}
              className="lg:col-span-2"
            >
              <Card className="border-slate-200 h-full shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl sm:text-3xl text-[#0B1D51] flex items-center gap-3">
                    <Target className="w-8 h-8 text-[#FF9F1C]" />
                    Why this platform exists
                  </CardTitle>
                </CardHeader>

                <CardContent className="space-y-5 text-[#475569] leading-relaxed text-base sm:text-lg">
                  <p>
                    Train driver recruitment is one of the most competitive
                    selection processes in the UK. Candidates are assessed against
                    safety-critical capabilities including attention, vigilance,
                    memory, reasoning, perception, reaction speed, coordination,
                    communication and behavioural suitability.
                  </p>

                  <p>
                    Many capable applicants reach the psychometric stage without
                    having trained these skills in a structured way. They may be
                    motivated, committed and suitable for the role, but still
                    underperform because the format, pace and pressure of the
                    assessment feel unfamiliar.
                  </p>

                  <p>
                    <strong className="text-[#0B1D51]">
                      TrainDriverPsychometrics.co.uk
                    </strong>{" "}
                    was created to give applicants a better way to prepare. Through
                    interactive practice, repetition and feedback, candidates can
                    build familiarity, improve relevant skills and approach the real
                    assessment process with greater confidence and control.
                  </p>
                </CardContent>
              </Card>
            </motion.div>

            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              transition={{ delay: 0.18 }}
            >
              <Card className="border-slate-200 h-full bg-gradient-to-br from-[#0B1D51] to-[#12306f] text-white shadow-sm">
                <CardHeader>
                  <CardTitle className="font-heading text-2xl flex items-center gap-3">
                    <TrendingUp className="w-7 h-7 text-[#FF9F1C]" />
                    Built to improve performance
                  </CardTitle>
                </CardHeader>

                <CardContent className="space-y-4 text-white/82 leading-relaxed">
                  <p>
                    We do not guarantee recruitment outcomes. Final decisions are
                    made by train operators, assessment providers and official
                    selection processes.
                  </p>

                  <p>
                    What we do provide is focused, repeated and interactive practice
                    designed to strengthen the skill areas assessed during train
                    driver psychometric selection.
                  </p>

                  <div className="rounded-xl bg-white/10 p-4 ring-1 ring-white/15">
                    <p className="font-semibold text-white mb-1">
                      Better preparation creates a better candidate.
                    </p>
                    <p className="text-sm">
                      Our aim is to help you practise harder, understand the process
                      better and walk into assessment with more confidence, accuracy
                      and control.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* What We Help With */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ delay: 0.22 }}
            className="mb-8"
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B1D51] mb-3">
              Practise the areas that matter
            </h2>
            <p className="text-[#64748B] max-w-3xl leading-relaxed">
              Train driver psychometric assessment is designed to measure the core
              abilities linked to safe, reliable train driving. Our platform helps
              candidates train those areas repeatedly before they face the real
              process.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {practiceAreas.map((item, index) => (
              <motion.div
                key={item.title}
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                transition={{ delay: 0.05 * index }}
              >
                <Card className="border-slate-200 h-full shadow-sm hover:shadow-md transition-shadow">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl text-[#0B1D51] flex items-start gap-3">
                      <item.icon className="w-6 h-6 text-[#FF9F1C] mt-1 shrink-0" />
                      {item.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[#64748B] leading-relaxed">{item.text}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Why Interactive Practice */}
        <section className="bg-white border-y border-slate-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
            <div className="grid lg:grid-cols-2 gap-8 items-start">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                transition={{ delay: 0.1 }}
              >
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0B1D51] mb-4">
                  Why interactive practice matters
                </h2>

                <div className="space-y-4 text-[#475569] leading-relaxed">
                  <p>
                    Research into aptitude and psychometric assessment retesting
                    has consistently demonstrated measurable practice effects.
                    Candidates who repeatedly practise structured assessment tasks
                    often improve familiarity, confidence, accuracy and overall
                    performance under timed conditions.
                  </p>

                  <p>
                    In safety-critical recruitment, performance is not simply about
                    intelligence. It is about maintaining concentration, applying
                    rules consistently, recognising patterns quickly, managing
                    workload and sustaining attention over prolonged periods —
                    all areas heavily referenced within railway human factors and
                    competence research, including RIS-3751-TOM guidance on train
                    driver selection and competence management.
                  </p>

                  <p>
                    Our interactive preparation tools are designed to help users
                    repeatedly practise skills relevant to real railway assessment
                    environments, including vigilance, sustained attention,
                    processing speed, reaction accuracy, memory, hand-eye
                    coordination and rule-based decision making.
                  </p>

                  <p>
                    Candidates can practise against assessment styles relevant to
                    commonly used railway psychometric testing formats such as
                    Group Bourdon concentration exercises, TEA-Occ style attention
                    tasks, vigilance-based assessments, memory exercises,
                    situational judgement testing and structured MMI preparation.
                  </p>

                  <p>
                    The objective is not to memorise answers or "game" the
                    assessment process. The objective is to build stronger
                    performance habits: sharper focus, improved consistency,
                    better control under pressure, reduced unforced errors and
                    greater familiarity with the type of mental workload expected
                    during modern train driver selection.
                  </p>
                </div>
              </motion.div>

              <motion.div
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                transition={{ delay: 0.18 }}
              >
                <Card className="border-slate-200 bg-[#F8FAFC] shadow-sm">
                  <CardHeader>
                    <CardTitle className="font-heading text-2xl text-[#0B1D51] flex items-center gap-3">
                      <Award className="w-7 h-7 text-[#2CB67D]" />
                      Our preparation principles
                    </CardTitle>
                  </CardHeader>

                  <CardContent>
                    <ul className="space-y-4">
                      <li className="flex gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#2CB67D] shrink-0 mt-0.5" />
                        <span className="text-[#475569] leading-relaxed">
                          Structured repetition helps candidates improve familiarity
                          and confidence within timed psychometric environments.
                        </span>
                      </li>

                      <li className="flex gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#2CB67D] shrink-0 mt-0.5" />
                        <span className="text-[#475569] leading-relaxed">
                          Interactive exercises target skills relevant to railway
                          assessments, including vigilance, concentration,
                          processing speed and rule application.
                        </span>
                      </li>

                      <li className="flex gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#2CB67D] shrink-0 mt-0.5" />
                        <span className="text-[#475569] leading-relaxed">
                          Practice supports consistency under pressure rather than
                          simple answer memorisation.
                        </span>
                      </li>

                      <li className="flex gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#2CB67D] shrink-0 mt-0.5" />
                        <span className="text-[#475569] leading-relaxed">
                          Our preparation reflects the behavioural, cognitive and
                          human factors demands associated with modern train driver
                          recruitment standards.
                        </span>
                      </li>

                      <li className="flex gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#2CB67D] shrink-0 mt-0.5" />
                        <span className="text-[#475569] leading-relaxed">
                          Relevant practice areas include Group Bourdon, TEA-Occ,
                          vigilance tasks, reaction testing, situational judgement
                          exercises and structured MMI preparation.
                        </span>
                      </li>
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        {/* TDF connection */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="border-slate-200 lg:col-span-2 shadow-sm">
              <CardHeader>
                <CardTitle className="font-heading text-2xl sm:text-3xl text-[#0B1D51] flex items-center gap-3">
                  <BookOpen className="w-8 h-8 text-[#FF9F1C]" />
                  Part of the wider Train Driver Foundation journey
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-4 text-[#475569] leading-relaxed">
                <p>
                  Psychometric assessment is only one part of becoming a train
                  driver. Candidates also need strong applications, realistic role
                  understanding, interview preparation, safety-critical awareness and
                  confidence across the full recruitment process.
                </p>

                <p>
                  Train Driver Psychometrics focuses on the testing stage, while the
                  wider Train Driver Foundation provides additional support with CVs,
                  applications, interviews, trainee driver guidance and rail-focused
                  preparation.
                </p>

                <a
                  href="https://www.traindriverfoundation.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#0B1D51] text-white px-6 py-3 rounded-xl hover:bg-[#0B1D51]/90 transition-colors font-semibold"
                >
                  <Users className="w-5 h-5" />
                  Explore Train Driver Foundation
                  <ExternalLink className="w-4 h-4" />
                </a>
              </CardContent>
            </Card>

            <Card className="border-amber-200 bg-amber-50 shadow-sm">
              <CardHeader>
                <CardTitle className="font-heading text-xl text-[#0B1D51] flex items-center gap-3">
                  <AlertTriangle className="w-6 h-6 text-[#FF9F1C]" />
                  Important note
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-3 text-[#475569] leading-relaxed">
                <p>
                  This platform is for practice and preparation. It is not an
                  official assessment centre and does not replace instructions,
                  practice material or guidance issued by a train operator or
                  assessment provider.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Final CTA */}
        <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-16 sm:pb-20">
          <Card className="border-slate-200 bg-gradient-to-br from-[#0B1D51] via-[#102967] to-[#0B1D51] text-white shadow-sm overflow-hidden">
            <CardContent className="p-8 sm:p-10">
              <div className="grid lg:grid-cols-[1.5fr_1fr] gap-8 items-center">
                <div>
                  <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-4">
                    Do not wait until assessment day to find out where you stand
                  </h2>

                  <p className="text-white/82 leading-relaxed text-lg">
                    Train driver psychometric assessments are competitive,
                    structured and safety-critical. Candidates who take preparation
                    seriously give themselves the best chance of performing at their
                    true level. Start practising the skills before the real process
                    puts them under pressure.
                  </p>
                </div>

                <div className="flex flex-col gap-3">
                  <a
                    href="/register"
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#FF9F1C] px-6 py-3 font-semibold text-[#0B1D51] hover:bg-[#ffb347] transition-colors"
                  >
                    Start practising now
                    <CheckCircle2 className="h-5 w-5" />
                  </a>

                  <a
                    href="/login"
                    className="inline-flex items-center justify-center gap-2 rounded-xl bg-white/10 px-6 py-3 font-semibold text-white ring-1 ring-white/20 hover:bg-white/15 transition-colors"
                  >
                    Log in to your account
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>
    </PageLayout>
  );
};

export default AboutPage;